import { useCallback, useEffect, useMemo, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getRecipientsData, getRecipientsState } from 'selectors/recipients';
import { useGetAdminRecipientsQuery } from 'services/adminRecipientsApi';
import { useGetShoppingCartQuery } from 'services/shoppingCartApi';
import { getRecipients } from 'store/recipients/actions';
import { RecipientsRequestType } from 'store/recipients/types';
import {
  CartProcessingRedirect,
  RecipientSelectionMode,
  ShoppingCartRecipientStatus,
} from 'store/shoppingCart/types';
import { NOT_IN_CART, NOT_SHOWN_ON_SITE, OATH_CLIENT_ID } from '../constants';
import { RecipientOption } from './types';

/**
 * The recipients a client's filter can pick from: the cart's own list in list mode, a search on
 * the cart's processor in criteria mode, and every public-searchable Stripe recipient for Oath.
 */
export const useFilterRecipientOptions = (
  clientId: number,
  filterRecipientIds: string[],
  { skip }: { skip: boolean }
) => {
  const dispatch = useAppDispatch();
  const isOath = clientId === OATH_CLIENT_ID;

  const {
    currentData: cartResponse,
    isLoading: isCartLoading,
    isError: isCartError,
    refetch: refetchCart,
  } = useGetShoppingCartQuery({ clientId }, { skip: skip || isOath });
  const cart = cartResponse?.cart;
  // Filters belong to a cart, so without one there is nothing to pick from or save to.
  const hasNoCart = !isOath && cartResponse !== undefined && !cart;
  const isListMode = cart?.recipient_selection_mode === RecipientSelectionMode.RECIPIENTS_LIST;
  const isCriteriaMode = cart?.recipient_selection_mode === RecipientSelectionMode.CRITERIA_MATCH;

  // A criteria cart without a processor searches every processor rather than the API's DE default.
  const searchProcessor = skip
    ? undefined
    : isOath
      ? CartProcessingRedirect.STRIPE
      : isCriteriaMode
        ? (cart?.page.processing_redirect ?? 'all')
        : undefined;

  const [searchAttempt, setSearchAttempt] = useState(0);
  const [isSearchDone, setIsSearchDone] = useState(false);
  const searchError = useAppSelector(state => getRecipientsState(state).error);
  const searchedRecipients = useAppSelector(getRecipientsData);

  useEffect(() => {
    if (!searchProcessor) return;
    // A search superseded by a client switch must not mark the newer one as done.
    let isCurrent = true;
    setIsSearchDone(false);
    dispatch(
      getRecipients({
        payment_processor: searchProcessor,
        is_public_searchable: isOath,
        request_type: RecipientsRequestType.admin,
      })
    ).then(() => {
      if (isCurrent) setIsSearchDone(true);
    });
    return () => {
      isCurrent = false;
    };
  }, [dispatch, searchProcessor, isOath, searchAttempt]);

  const isSearchPending = Boolean(searchProcessor) && !isSearchDone;
  const hasSearchError = Boolean(searchProcessor) && isSearchDone && Boolean(searchError);

  // The shared recipients slice still holds whatever another screen loaded until this search ends.
  const options = useMemo<RecipientOption[]>(() => {
    if (isListMode) {
      return (cart?.recipients ?? [])
        .filter(
          recipient =>
            recipient.shopping_cart_recipient_status === ShoppingCartRecipientStatus.ACTIVE
        )
        .map(recipient => ({ value: recipient.recipient_id, label: recipient.recipient_name }));
    }
    if (!searchProcessor || !isSearchDone || searchError) return [];
    return searchedRecipients.map(recipient => ({
      value: recipient.recipient_id,
      label: recipient.recipient_name || recipient.recipient_id,
    }));
  }, [isListMode, cart, searchProcessor, isSearchDone, searchError, searchedRecipients]);

  const knownNames = useMemo(() => {
    const names = new Map(options.map(option => [option.value, option.label]));
    cart?.recipients.forEach(recipient =>
      names.set(recipient.recipient_id, recipient.recipient_name)
    );
    return names;
  }, [options, cart]);

  const hasUnknownNames = filterRecipientIds.some(recipientId => !knownNames.has(recipientId));
  // First loads only: a background refetch must not unmount the form and drop the admin's edits.
  const isOptionsPending = isCartLoading || isSearchPending;
  const {
    currentData: allRecipients,
    isLoading: isNamesLoading,
    isError: isNamesError,
    refetch: refetchNames,
  } = useGetAdminRecipientsQuery(undefined, { skip: skip || isOptionsPending || !hasUnknownNames });

  const availableIds = useMemo(() => new Set(options.map(option => option.value)), [options]);
  const unavailableReason = isListMode ? NOT_IN_CART : NOT_SHOWN_ON_SITE;

  const toRecipientOption = useCallback(
    (recipientId: string): RecipientOption => {
      const label =
        knownNames.get(recipientId) ??
        allRecipients?.find(recipient => recipient.recipient_id === recipientId)?.recipient_name ??
        recipientId;
      return availableIds.has(recipientId)
        ? { value: recipientId, label }
        : { value: recipientId, label, unavailableReason };
    },
    [knownNames, allRecipients, availableIds, unavailableReason]
  );

  const failedSource = isCartError
    ? 'shopping_cart'
    : hasSearchError
      ? 'recipients_search'
      : isNamesError
        ? 'recipient_names'
        : undefined;

  useEffect(() => {
    if (!failedSource) return;
    datadogLogs.logger.error('Failed to load filter recipient options', {
      client_id: clientId,
      failed_source: failedSource,
    });
  }, [failedSource, clientId]);

  const retry = () => {
    if (isCartError) refetchCart();
    if (hasSearchError) setSearchAttempt(attempt => attempt + 1);
    if (isNamesError) refetchNames();
  };

  return {
    options,
    toRecipientOption,
    showsCriteriaNote: isCriteriaMode,
    isLoading: isOptionsPending || isNamesLoading,
    hasError: Boolean(failedSource),
    retry,
    hasNoCart,
    clientName: cartResponse?.client_name,
  };
};
